<template>
	<Application class="ChangeBookingsView" :errorDetail="errorDetail" v-model:errorTitle="errorTitle"
		:successDetail="successDetail" v-model:successTitle="successTitle" :loading="loading || submitLoading"
		:style="{ 'padding-left': tokenIsValid && isSideBarFixed && !isMobile ? '332px' : '32px' }"
	>
		<template #navbar>
			<h1 v-if="order" class="order-title">{{ $t('text.order') }} #{{ order.orderIncrementId }}</h1>
			<v-spacer></v-spacer>
		</template>

		<InvalidTokenView v-if="initialLoadError" v-bind="initialLoadErrorView" />

		<template v-else-if="tokenIsValid">
			<SideBar class="rebooking-sidebar" :class="{ mobileSidebar: isMobile }">
				<template #customActions>
					<div class="pa-4 flex-column d-flex" style="gap: 12px;">
						<div class="app-title">
							<v-icon size="28" color="#00aeef">mdi-calendar-sync</v-icon>
							<div>
								<div class="app-title-small">MyServices</div>
								<div class="app-title-main">{{ $t('text.changeBookingsTitle') }}</div>
							</div>
						</div>
						<v-btn class="greenButton" theme="dark" elevation="0" prepend-icon="mdi-update"
							:disabled="!canSubmit" :loading="submitting" data-cy="updateOrder" @click="onUpdateOrder">
							{{ $t('text.updateOrder') }}
						</v-btn>
					</div>
				</template>
				<template #customNavigation>
					<div class="pb-4 flex-column d-flex sidebar-navigation">
						<div class="nav-item active">{{ $t('text.orderDetails') }}</div>
						<div class="language-switcher">
							<v-menu location="top">
								<template #activator="{ props }">
									<div v-bind="props" class="language-activator" data-cy="languageSwitcher">
										<LanguageFlag :modelValue="currentLocale" />
										<span>{{ languageName(currentLocale) }}</span>
										<v-icon size="18">mdi-chevron-up</v-icon>
									</div>
								</template>
								<v-list>
									<v-list-item v-for="language in languages" :key="language.code"
										:active="language.code === currentLocale"
										:data-cy="`language-${language.code}`"
										@click="setLocale(language.code)"
									>
										<template #prepend>
											<LanguageFlag :modelValue="language.code" style="margin-right: 8px" />
										</template>
										{{ language.name }}
									</v-list-item>
								</v-list>
							</v-menu>
						</div>
					</div>
				</template>
			</SideBar>

			<div style="margin-top: 76px;">
				<v-skeleton-loader v-if="!rows.length && loading" type="table-row@4" />
				<OrderDetailsCard v-else
					:rows="rows"
					:customer="order?.customer"
					:min-date="minDate"
					:notify-service-providers="notifyServiceProviders"
					:failure-message="failureBannerText"
					@toggle="toggleRow"
					@undo="undoRow"
					@update:draft="updateDraft"
					@update:month="onMonthChange"
					@update:notifyServiceProviders="notifyServiceProviders = $event"
				/>
			</div>

			<PriceChangeDialog ref="priceChangeDialog"
				:notify-service-providers="notifyServiceProviders"
				@update:notifyServiceProviders="notifyServiceProviders = $event"
			/>
			<ResultDialogs ref="resultDialogs" />
		</template>
	</Application>
</template>

<script>
import Common from '../../../mixins/Common.vue'
import Loading from '../../../mixins/Loading.vue'
import Toast from '@/mixins/Toast.vue'
import Application from '../Application.vue'
import SideBar from '../../../components/common/SideBar.vue'
import LanguageFlag from '../../../components/common/LanguageFlag.vue'
import RebookingExecutive from '../../../../../api2/src/modules/rebooking/RebookingExecutive'
import { ForbiddenError, UnauthorizedError, NotFoundError } from '../../../../../api2/src/framework/rmi-errors'
import { ServerUnavailableError } from '../../../../../api2/src/framework/rmi-client-util'
import InvalidTokenView from './InvalidTokenView.vue'
import OrderDetailsCard from './OrderDetailsCard.vue'
import PriceChangeDialog from './PriceChangeDialog.vue'
import ResultDialogs from './ResultDialogs.vue'
import { buildDateTime, buildDraft, deepClone, deepEqual, isValidEmail, pollRebookingJob, reasonKeyForCode, slotFree } from './rebooking-utils'

// background rebooking job (MYS-7377 phase 2): poll cadence and how long the page waits before
// telling the user to reload later (the job keeps running server-side)
const JOB_POLL_INTERVAL_MS = 2000
const JOB_POLL_CAP_MS = 10 * 60 * 1000
const POLL_INTERVAL_MS = 5000
const PRICE_CHECK_DEBOUNCE_MS = 400

export default {
	name: 'ChangeBookingsView',
	components: { Application, SideBar, LanguageFlag, InvalidTokenView, OrderDetailsCard, PriceChangeDialog, ResultDialogs },
	mixins: [ Common, Loading, Toast ],
	props: {
		token: { type: String, required: true },
	},
	data() {
		return {
			tokenIsValid: false,
			initialLoadError: null,
			loading: false,
			submitLoading: false,
			submitting: false,
			order: null,
			rows: [],
			notifyServiceProviders: true,
			// banner failure state — stored as raw error (not resolved text) so it re-translates
			// reactively when the locale changes
			bannerError: null, // { code, message } | null
			bannerGeneric: false,
			executive: new RebookingExecutive(this),
			// non-visual bookkeeping (rowId -> handle); kept in data for simplicity, never rendered
			pollHandles: {},
			pollInFlight: {},
			pollQueued: {},
			priceCheckTimers: {},
		}
	},
	computed: {
		languages() {
			return [
				{ code: 'de', name: 'Deutsch' },
				{ code: 'en', name: 'English' },
				{ code: 'fr', name: 'Français' },
				{ code: 'it', name: 'Italiano' },
				{ code: 'nl', name: 'Nederlands' },
			]
		},
		currentLocale() {
			return this.$store.state.selectedLocale ?? this.$root.$i18n.locale ?? 'en'
		},
		minDate() {
			// same-day rebooking is allowed (MYS-7258); the backend's evaluateRebookability is
			// what actually enforces the finer-grained rule (a timed item can't be rebooked once
			// its own timeslot has started) - this only bounds the calendar to "not in the past".
			// Computed in Europe/Vienna, not the browser's local zone, to match the backend.
			return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Vienna' }).format(new Date())
		},
		dirtyRows() {
			return this.rows.filter(row => row.item.rebookable && !deepEqual(row.draft, row.original))
		},
		canSubmit() {
			return !this.submitting
				&& this.dirtyRows.length > 0
				&& this.dirtyRows.every(row => this.rowIsValid(row))
		},
		// resolved reactively so the banner re-translates on locale change
		failureBannerText() {
			if (this.bannerGeneric) return this.$t('text.rebookingItemsFailed')
			if (this.bannerError) return this.reasonText(this.bannerError)
			return ''
		},
		initialLoadErrorView() {
			if (this.initialLoadError === 'system') {
				return {
					title: 'We cannot load your booking right now',
					detail: 'One of our systems is currently unavailable. Please try again later.',
					icon: 'mdi-alert-circle-outline',
					iconColor: '#f34545',
					dataCy: 'systemError',
				}
			}
			return {}
		},
	},
	async mounted() {
		window.addEventListener('beforeunload', this.onBeforeUnload)
		this.initLocale()
		await this.loadOrder()
	},
	beforeUnmount() {
		window.removeEventListener('beforeunload', this.onBeforeUnload)
		this.stopAllPolling()
	},
	beforeRouteLeave() {
		// unsaved edits, or an update still running in the background job (MYS-7377 phase 2)
		if (this.dirtyRows.length || this.submitting) {
			return window.confirm(this.$t('text.unsavedChangesWarning'))
		}
		return true
	},
	methods: {
		onBeforeUnload(event) {
			if (this.dirtyRows.length || this.submitting) {
				event.preventDefault()
				event.returnValue = ''
			}
		},

		// ----- language ------------------------------------------------------
		// no logged-in user on the standalone page: default to the browser language
		initLocale() {
			const browserLocale = (navigator.language ?? '').substring(0, 2).toLowerCase()
			if (this.languages.some(language => language.code === browserLocale)) {
				this.setLocale(browserLocale)
			}
		},
		setLocale(locale) {
			// same mechanics as the NavBar language menu
			this.$root.$i18n.locale = locale
			this.$vuetify.locale.current = locale
			this.$store.commit('setSelectedLocale', locale)
		},
		languageName(code) {
			return this.languages.find(language => language.code === code)?.name ?? code
		},

		// ----- loading -------------------------------------------------------
		async loadOrder() {
			if (!this.token) {
				this.initialLoadError = 'link'
				return
			}
			this.loading = true
			this.initialLoadError = null
			try {
				const order = await this.executive.getRebookableOrderByToken(this.token)
				if (this.isLinkError(order)) throw order
				this.order = order
				this.rows = order.items.map(item => ({
					item,
					original: buildDraft(item),
					draft: buildDraft(item),
					expanded: false,
					contingents: null,
					contingentsLoading: false,
					priceDiff: null,
					// PEAK persists product_options.updated_info after a successful rebook
					result: item.updatedInfo ? 'updated' : null,
				}))
				this.tokenIsValid = true
			}
			catch (error) {
				if (this.isLinkError(error)) {
					this.initialLoadError = 'link'
				}
				else {
					console.error(error)
					this.initialLoadError = 'system'
				}
			}
			this.loading = false
		},
		// a link error discovered mid-session (token expired, feature disabled, order gone) —
		// retrying can never succeed, so switch to the invalid-link screen instead of toasting
		failLink() {
			this.stopAllPolling()
			this.tokenIsValid = false
			this.initialLoadError = 'link'
		},
		handleBackgroundError(error, logLabel) {
			if (this.isLinkError(error)) return this.failLink()
			console.error(logLabel, error)
		},
		isLinkError(error) {
			if (error instanceof UnauthorizedError || error instanceof ForbiddenError || error instanceof NotFoundError) return true
			if (['UNAUTHORIZED', 'FORBIDDEN', 'NOT_FOUND'].includes(error?.code)) return true
			return [
				'errorTokenExpired',
				'errorTokenInvalid',
				'errorTokenInvalidPurpose',
				'errorClientNotFound',
				'errorOrderNotFound',
			].includes(error?.message)
		},

		// ----- row state -----------------------------------------------------
		rowIsValid(row) {
			// email is only required when the PEAK person actually has an email attribute (else the
			// field isn't shown and isn't sent)
			const person = row.item.personalization ?? {}
			if (person.email !== undefined && !isValidEmail(row.draft.email)) return false
			// timeslot required (with places left) when the selected date has timed slots
			if (row.draft.date && row.contingents) {
				const day = row.contingents.find(candidate => candidate.date === row.draft.date)
				// availability for the selected date not loaded (yet) — never skip the timeslot
				// validation just because the data is missing
				if (!day) return false
				const timed = (day.slots ?? []).filter(slot => slot.time !== '00:00')
				if (timed.length) {
					const selected = timed.find(slot => slot.time === row.draft.time)
					if (!selected || slotFree(selected) <= 0) return false
				}
			}
			// additional personalization config (mirrors the backend validation) — only the fields
			// the PEAK person actually has (and that we therefore render)
			const presentAdditional = person.additional ?? {}
			for (const field of row.item.personalizationConfig?.additional ?? []) {
				if (!(field.id in presentAdditional)) continue
				const value = row.draft.additional?.[field.id]
				if (field.required && (value === undefined || value === null || String(value).trim() === '')) return false
				if (field.pattern && value) {
					try { if (!new RegExp(field.pattern).test(String(value))) return false } catch { /* tolerate bad config */ }
				}
			}
			return true
		},
		toggleRow(row) {
			row.expanded = !row.expanded
			if (row.expanded) {
				this.startPolling(row)
			}
			else {
				this.stopPolling(row)
			}
		},
		undoRow(row) {
			row.draft = deepClone(row.original)
			row.priceDiff = null
			row.expanded = false
			this.stopPolling(row)
		},
		updateDraft(row, patch) {
			const dateChanged = 'date' in patch && patch.date !== row.draft.date
			Object.assign(row.draft, patch)
			if (dateChanged && patch.date) this.schedulePriceCheck(row)
		},

		// ----- contingents / polling -----------------------------------------
		defaultRange(row) {
			const base = row.draft.date ?? row.item.consumptionDate ?? this.minDate
			const year = Number(base.substring(0, 4))
			const month = Number(base.substring(5, 7))
			const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate()
			return { from: `${base.substring(0, 7)}-01`, until: `${base.substring(0, 7)}-${String(lastDay).padStart(2, '0')}` }
		},
		// Polling always fetches the month of the SELECTED date (defaultRange keys off draft.date),
		// so validation data can never drift to a month the user merely browsed. Browsing months
		// passes an explicit one-off `range` instead; its days are merged in for display.
		async fetchContingents(row, range) {
			if (!row.item.productId) return
			const rowId = row.item.orderItemId
			if (this.pollInFlight[rowId]) {
				// remember the latest dropped request and replay it once the in-flight one ends,
				// so rapid month-paging doesn't leave the calendar on stale data for a poll tick
				this.pollQueued[rowId] = range ?? null
				return
			}
			this.pollInFlight[rowId] = true
			row.contingentsLoading = row.contingents === null
			try {
				const effective = range ?? this.defaultRange(row)
				const days = await this.executive.getRebookingAvailabilities(this.token, row.item.productId, effective.from, effective.until)
				row.contingents = this.mergeContingents(row.contingents, days)
			}
			catch (error) {
				this.handleBackgroundError(error, 'could not load contingents')
			}
			finally {
				this.pollInFlight[rowId] = false
				row.contingentsLoading = false
				if (rowId in this.pollQueued) {
					const queued = this.pollQueued[rowId]
					delete this.pollQueued[rowId]
					this.fetchContingents(row, queued ?? undefined)
				}
			}
		},
		// replace fetched days by date, keep the rest — the poll only refreshes the selected
		// month while browsed months stay displayable
		mergeContingents(existing, days) {
			if (!existing) return days ?? null
			const byDate = new Map(existing.map(day => [day.date, day]))
			for (const day of days ?? []) byDate.set(day.date, day)
			return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date))
		},
		onMonthChange(row, range) {
			this.fetchContingents(row, range)
		},
		startPolling(row) {
			const rowId = row.item.orderItemId
			this.stopPolling(row)
			this.fetchContingents(row)
			this.pollHandles[rowId] = setInterval(() => {
				if (document.hidden) return
				this.fetchContingents(row)
			}, POLL_INTERVAL_MS)
		},
		stopPolling(row) {
			const rowId = row.item.orderItemId
			if (this.pollHandles[rowId]) {
				clearInterval(this.pollHandles[rowId])
				delete this.pollHandles[rowId]
			}
		},
		stopAllPolling() {
			for (const handle of Object.values(this.pollHandles)) clearInterval(handle)
			this.pollHandles = {}
			for (const timer of Object.values(this.priceCheckTimers)) clearTimeout(timer)
			this.priceCheckTimers = {}
		},

		// ----- price diff -----------------------------------------------------
		schedulePriceCheck(row) {
			const rowId = row.item.orderItemId
			clearTimeout(this.priceCheckTimers[rowId])
			this.priceCheckTimers[rowId] = setTimeout(async () => {
				try {
					const diffs = await this.executive.checkRebookingPriceDiff(this.token, [{ orderItemId: rowId, newDate: row.draft.date }])
					row.priceDiff = diffs?.[0] ?? null
				}
				catch (error) {
					// background hint only — the authoritative check runs on submit
					this.handleBackgroundError(error, 'price check failed')
					row.priceDiff = null
				}
			}, PRICE_CHECK_DEBOUNCE_MS)
		},
		enrichDiffs(diffs) {
			return diffs.map(diff => {
				const row = this.rows.find(candidate => candidate.item.orderItemId === diff.orderItemId)
				return {
					...diff,
					name: row?.item?.name,
					ticketType: row?.item?.ticketType,
					ticketCategory: row?.item?.ticketCategory,
					currency: row?.item?.currency,
				}
			})
		},

		// ----- submit ---------------------------------------------------------
		buildChanges() {
			return this.dirtyRows.map(row => ({
				orderItemId: row.item.orderItemId,
				dateTime: buildDateTime(row.draft.date, row.draft.time),
				personalization: {
					firstName: row.draft.firstName,
					lastName: row.draft.lastName,
					email: row.draft.email,
					additional: { ...row.draft.additional },
				},
			}))
		},
		async onUpdateOrder() {
			if (!this.canSubmit) return
			this.submitting = true
			this.bannerError = null
			this.bannerGeneric = false
			for (const row of this.rows) this.stopPolling(row)

			try {
				const changes = this.buildChanges()

				// authoritative price gate before submitting
				const diffs = await this.executive.checkRebookingPriceDiff(this.token, changes.map(change => ({
					orderItemId: change.orderItemId,
					newDate: change.dateTime.substring(0, 10),
				})))
				let confirmPriceChange = false
				if (diffs.some(diff => diff.warning)) {
					confirmPriceChange = await this.$refs.priceChangeDialog.open(this.enrichDiffs(diffs.filter(diff => diff.warning)))
					if (!confirmPriceChange) return
				}

				await this.submitChanges(changes, confirmPriceChange)
			}
			catch (error) {
				if (this.isLinkError(error)) return this.failLink()
				console.error(error)
				// the server did not answer the submit (empty 500 from a proxy/LB timeout) and the RMI
				// client deliberately did not retry (MYS-7377): the rebooking may or may not have been
				// performed — tell the user to reload and check before trying again
				if (error instanceof ServerUnavailableError) {
					this.showErrorToast('rebookingServerUnavailable')
					return
				}
				// another update with different changes is still running for this order
				if (error?.code === 'REBOOKING_IN_PROGRESS') {
					this.showErrorToast('rebookingInProgress')
					return
				}
				// transport / unexpected backend error (e.g. PEAK 500). error.message is already
				// human-readable and not a translation key, so pass isTranslated=true.
				const detail = error?.message || this.$t('text.rebookingFailedGeneric')
				this.showErrorToast(detail, true)
			}
			finally {
				this.submitting = false
				await this.refreshContingentsAfterSubmit()
			}
		},
		async submitChanges(changes, confirmPriceChange) {
			let result
			this.submitLoading = true
			try {
				result = await this.executive.submitRebooking(this.token, {
					notifyServiceproviders: this.notifyServiceProviders,
					confirmPriceChange,
					items: changes,
				})
			}
			finally {
				this.submitLoading = false
			}

			// server-side detected an unconfirmed price change (TOCTOU) — re-open the modal
			if (result.overallStatus === 'PRICE_CHANGED') {
				const confirmed = await this.$refs.priceChangeDialog.open(this.enrichDiffs((result.priceDiffs ?? []).filter(diff => diff.warning)))
				if (!confirmed) return
				return await this.submitChanges(changes, true)
			}

			// the PEAK call runs in a background job (MYS-7377 phase 2): PEAK needs 11–14 s per order
			// item, far beyond the edge timeout. Poll the job; the page stays in the updating state.
			if (result.overallStatus === 'PENDING') {
				result = await this.waitForRebookingJob(result.jobId)
				if (!result) return // still running after the cap — user was told to reload later
			}

			await this.handleSubmitResult(result)
		},
		// waits for the background job (MYS-7377 phase 2). Returns the result when DONE, otherwise
		// null after telling the user: outcome unknown (job ERROR / not found — PEAK may have
		// committed, so "reload and check", never "failed") or still running at the cap.
		// Link errors (token expired) propagate to onUpdateOrder, which shows the invalid-link view.
		async waitForRebookingJob(jobId) {
			this.submitLoading = true
			try {
				const poll = await pollRebookingJob({
					getStatus: () => this.executive.getRebookingJobStatus(this.token, jobId),
					isLinkError: error => this.isLinkError(error),
					intervalMs: JOB_POLL_INTERVAL_MS,
					capMs: JOB_POLL_CAP_MS,
					onPollError: error => console.warn('rebooking job poll failed, polling again', error),
				})
				if (poll.outcome === 'done') return poll.result
				if (poll.outcome === 'unknown') {
					console.error('rebooking job outcome unknown', poll.error)
					this.showErrorToast('rebookingServerUnavailable')
				}
				else {
					this.showWarningToast('rebookingStillProcessing')
				}
				return null
			}
			finally {
				this.submitLoading = false
			}
		},
		// localized reason for a PEAK error/warning code (same resolution as the result dialog):
		// mapped code -> translation; else PEAK's raw message; else a generic reason.
		reasonText(error) {
			if (!error) return this.$t('text.rebookGenericItemError')
			const key = reasonKeyForCode(error.code)
			if (key) return this.$t('text.' + key)
			return error.message || this.$t('text.rebookGenericItemError')
		},
		// sets the banner state for the failure list: the shared reason when every affected item
		// failed for the same code, otherwise a neutral "some items could not be rebooked".
		// stores raw state (not resolved text) so the computed banner re-translates on locale change.
		setBannerForFailures(failedEntries) {
			const codes = new Set(failedEntries.map(entry => entry.error?.code).filter(Boolean))
			if (codes.size === 1 && failedEntries.length) {
				this.bannerError = failedEntries.find(entry => entry.error)?.error
				this.bannerGeneric = false
			}
			else {
				this.bannerError = null
				this.bannerGeneric = true
			}
		},
		async handleSubmitResult(result) {
			// top-level PEAK rejection (order not found, wrong status, auth, ...) — the single PEAK
			// request failed as a whole. A requestError can still come with UNCHANGED items (already
			// applied, never sent); then fall through to the per-item handling so those are not
			// shown as failed.
			const anyApplied = (result.items ?? []).some(item => item.status === 'REBOOKED' || item.status === 'UNCHANGED')
			if (result.requestError && !anyApplied) {
				this.showErrorToast('rebookingItemsFailed')
				await this.$refs.resultDialogs.open('failed', { requestError: result.requestError })
				this.bannerError = result.requestError
				for (const row of this.dirtyRows) row.result = 'noAvailability'
				return
			}

			const resultsById = new Map(result.items.map(item => [item.orderItemId, item]))
			const rebookedRows = []
			const failedEntries = [] // { row, error }
			let cancelFailed = false

			for (const row of this.dirtyRows) {
				const itemResult = resultsById.get(row.item.orderItemId)
				if (!itemResult) continue
				// UNCHANGED: the order already had the requested values (e.g. a repeated submit after
				// a retry, MYS-7377) — nothing was sent to PEAK, treat it as applied
				if (itemResult.status === 'REBOOKED' || itemResult.status === 'UNCHANGED') {
					rebookedRows.push(row)
					// CANCELLATION_FAILED per the final PEAK spec (support case, UC-07)
					if (itemResult.warnings?.some(warning => warning.code === 'CANCELLATION_FAILED')) cancelFailed = true
				}
				else {
					// carry the per-item error so the dialog can show the real reason
					// (DATE_IN_PAST, OUTSIDE_BOOKING_WINDOW, NO_AVAILABILITY, ...)
					// PEAK rejected the request as a whole: the reason is in requestError, not per item
					failedEntries.push({ row, error: itemResult.error?.code === 'NO_RESPONSE' && result.requestError ? result.requestError : (itemResult.error ?? null) })
				}
			}

			if (failedEntries.length || result.overallStatus === 'FAILED' || result.overallStatus === 'NO_AVAILABILITY') {
				this.showErrorToast('rebookingItemsFailed')
				await this.$refs.resultDialogs.open('failed', {
					affectedItems: failedEntries.map(entry => ({ item: entry.row.item, error: entry.error })),
				})
				this.setBannerForFailures(failedEntries)
				for (const entry of failedEntries) entry.row.result = 'noAvailability'
			}

			if (rebookedRows.length) {
				if (cancelFailed) {
					this.showWarningToast('rebookingCancelOldFailed')
				}
				else {
					this.showSuccessToast('orderUpdatedSuccessfully')
				}
				await this.$refs.resultDialogs.open(cancelFailed ? 'cancelFailed' : 'success')
				await this.applyRebookedRows(rebookedRows)
			}
		},
		async applyRebookedRows(rebookedRows) {
			const rebookedIds = new Set(rebookedRows.map(row => row.item.orderItemId))
			// reload the order so item data + snapshots reflect the new booking
			try {
				const order = await this.executive.getRebookableOrderByToken(this.token)
				this.order = order
				for (const row of this.rows) {
					const freshItem = order.items.find(item => item.orderItemId === row.item.orderItemId)
					if (freshItem) row.item = freshItem
					if (rebookedIds.has(row.item.orderItemId)) {
						row.original = buildDraft(row.item)
						row.draft = buildDraft(row.item)
						row.expanded = false
						row.priceDiff = null
						row.result = 'updated'
						this.stopPolling(row)
					}
				}
			}
			catch (error) {
				console.error('could not refresh order after rebooking', error)
			}
		},
		async refreshContingentsAfterSubmit() {
			// PEAK mutated the Contingent Service DB — never show pre-submit availability numbers
			for (const row of this.rows) {
				if (!row.expanded) {
					row.contingents = null
					continue
				}
				row.contingents = null
				// startPolling fetches immediately before starting the interval
				this.startPolling(row)
			}
		},
	},
}
</script>

<style scoped lang="scss">
.ChangeBookingsView {
	padding-right: 32px;
	padding-bottom: 32px !important;

	.order-title {
		font-size: 20px;
		font-weight: 600;
	}

	.rebooking-sidebar {
		&.mobileSidebar {
			position: fixed;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			z-index: 100;
		}
	}

	.app-title {
		display: flex;
		align-items: center;
		gap: 10px;

		.app-title-small {
			font-size: 11px;
			color: #8f8f8f;
			line-height: 1.2;
		}
		.app-title-main {
			font-size: 16px;
			font-weight: 600;
			color: #00aeef;
			line-height: 1.2;
		}
	}

	.sidebar-navigation {
		height: 100%;
	}

	.nav-item {
		padding: 0 28px;
		height: 60px;
		display: flex;
		align-items: center;
		font-size: 15px;
		cursor: default;

		&.active {
			background-color: #00aeef;
			color: white;
			font-weight: 600;
		}
	}

	.language-switcher {
		margin-top: auto;
		padding: 16px 28px 60px 28px;

		.language-activator {
			display: flex;
			align-items: center;
			gap: 8px;
			cursor: pointer;
			font-size: 15px;
			color: black;
		}
	}
}
</style>
